import { type MiniMapNodeProps, useInternalNode } from "@xyflow/react";
import type { EntityDto } from "api/types";
import React, { useCallback, useState } from "react";

import { fitShapeBox, getMiniMapShape, LINKED_TREE_SHAPE } from "./minimapShapes";
import { getInitials } from "./nodeInitials";

import styles from "./MiniMapNode.module.css";

/** In flow units, so the outline stays a hairline at whatever scale the minimap is drawn at. */
const STROKE_WIDTH = 3;

/** Initials height as a share of the shape's shorter side: two letters fit inside even the narrow pin. */
const INITIALS_SCALE = 0.4;

/**
 * Minimap node: the entity's canvas silhouette and colour, carrying its image where it has one and
 * its initials where it does not, as the canvas node does. Without the initials a tree with no
 * pictures showed a row of identical blank shapes (TTWEB-48). Titles stay off: too long to read here.
 */
const MiniMapNode = ({ height, id, width, x, y }: MiniMapNodeProps) => {
	const node = useInternalNode(id);
	const entity = (node?.data as { entity?: EntityDto } | undefined)?.entity;
	/*
	 * A broken image otherwise paints the browser's placeholder glyph inside the silhouette, which the
	 * fill behind it can't hide. Remembering which URL failed rather than a bare flag lets the node try
	 * again once it points at a different image.
	 */
	const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
	const shape = node?.type === "linkedTree" ? LINKED_TREE_SHAPE : getMiniMapShape(entity?.type);
	const box = fitShapeBox(shape, x, y, width, height);
	const path = shape.toPath(box.x, box.y, box.width, box.height);
	const imageUrl = entity?.imageUrl;
	const onImageError = useCallback((): void => setFailedImageUrl(imageUrl ?? null), [imageUrl]);

	if (!imageUrl || imageUrl === failedImageUrl) {
		const outline = <path d={path} fill={shape.fill} stroke={shape.stroke} strokeWidth={STROKE_WIDTH} />;
		// A linked tree has no entity, and its evergreen glyph already says what it is.
		if (!entity) {
			return outline;
		}

		return (
			<g>
				{outline}
				<text
					className={styles.initials}
					dominantBaseline="central"
					fontSize={Math.min(box.width, box.height) * INITIALS_SCALE}
					textAnchor="middle"
					x={box.x + box.width / 2}
					y={box.y + box.height * (shape.labelCenterY ?? 0.5)}>
					{getInitials(entity.title)}
				</text>
			</g>
		);
	}

	const clipId = `minimap-clip-${id}`;

	return (
		<g>
			<clipPath id={clipId}>
				<path d={path} />
			</clipPath>
			{/* Behind the image the way the canvas gradient sits behind it, for transparent or slow images. */}
			<path d={path} fill={shape.fill} />
			<image
				clipPath={`url(#${clipId})`}
				height={box.height}
				href={imageUrl}
				// eslint-disable-next-line react/no-unknown-property -- valid on SVG <image>; the rule only lists HTML tags
				onError={onImageError}
				preserveAspectRatio="xMidYMid slice"
				width={box.width}
				x={box.x}
				y={box.y}
			/>
			<path d={path} fill="none" stroke={shape.stroke} strokeWidth={STROKE_WIDTH} />
		</g>
	);
};

export default MiniMapNode;
